// web/src/features/account/sportsPaySignUp/announceSportsPayCallback.ts
import {
  isSportsPayCallback,
  readSportsPayCallbackParams,
  SPORTS_PAY_COMPLETE_MESSAGE,
} from '@sportninja/common/features/account/sportsPaySignUp'

/**
 * Tells the opener that the SportsPay registration finished.
 *
 * SportsPay's `CallbackURL` points at OUR org page
 * (`…/organization/{id}?origin=onboarding_sportspay`). It was designed as a
 * top-level redirect, but we open the registration in an iframe, so the
 * callback loads a second copy of this app INSIDE that frame instead.
 *
 * Since it is our own page on our own origin, it can announce completion
 * itself — no backend or SportsPay change needed. This runs at app entry,
 * BEFORE React mounts, so completion is reported at the earliest possible
 * moment; the parent then closes the frame.
 *
 * The caller still renders the app normally afterwards. Skipping the render
 * would be a little cheaper, but it would leave a BLANK frame for any other
 * embed that ever loaded a URL carrying this param — and this app is
 * legitimately framed elsewhere (the plugin/widget embed). A brief render of a
 * frame that is about to close is the safer trade.
 *
 * A no-op unless we are actually framed AND the callback param is present, so
 * a normal page load never posts anything.
 *
 * @returns true when a message was posted. Informational only.
 */
export const announceSportsPayCallback = (): boolean => {
  // `window.parent === window` at the top level; only a framed copy announces.
  if (typeof window === 'undefined' || window.parent === window) {
    return false
  }

  if (!isSportsPayCallback(window.location.search)) {
    return false
  }

  /*
   * Forwarded whole rather than filtered. SportsPay appends `code` (the
   * merchant id) and `status` on top of the `origin` we asked for, and the
   * parent needs both for the completion PUT — but carrying the entire bag
   * means a param they add later arrives without a change here.
   */
  const params = readSportsPayCallbackParams(window.location.search)

  try {
    // Targeted at our own origin rather than '*', so the message cannot be read
    // by any other frame that happens to be listening.
    window.parent.postMessage(
      { type: SPORTS_PAY_COMPLETE_MESSAGE, params },
      window.location.origin
    )
    return true
  } catch {
    // A cross-origin parent (or a sandbox without allow-same-origin) throws.
    // The parent's own close/visibility refetches still cover completion, so
    // failing silently is better than breaking the framed page.
    return false
  }
}
